import type { ImageSegmenter, ImageSegmenterResult, MPMask } from '@mediapipe/tasks-vision';
import type { VideoBackgroundEffect } from '../types/meeting.types.ts';
import { segmentPersonFrame } from './personSegmentation.ts';

type ProcessorBuffers = {
  bgCanvas: HTMLCanvasElement;
  bgCtx: CanvasRenderingContext2D;
  personCanvas: HTMLCanvasElement;
  personCtx: CanvasRenderingContext2D;
  maskCanvas: HTMLCanvasElement;
  maskCtx: CanvasRenderingContext2D;
  softMaskCanvas: HTMLCanvasElement;
  softMaskCtx: CanvasRenderingContext2D;
  maskImageData: ImageData;
};

type MaskSelection = {
  mask: MPMask;
  mode: 'category' | 'confidence';
  invert: boolean;
};

function drawCover(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  dx: number,
  dy: number,
  dw: number,
  dh: number,
  mirrored: boolean
) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return;

  const videoRatio = vw / vh;
  const rectRatio = dw / dh;
  let sx = 0;
  let sy = 0;
  let sw = vw;
  let sh = vh;

  if (videoRatio > rectRatio) {
    sw = vh * rectRatio;
    sx = (vw - sw) / 2;
  } else {
    sh = vw / rectRatio;
    sy = (vh - sh) / 2;
  }

  ctx.save();
  if (mirrored) {
    ctx.translate(dx + dw, dy);
    ctx.scale(-1, 1);
    ctx.drawImage(video, sx, sy, sw, sh, dx, dy, dw, dh);
  } else {
    ctx.drawImage(video, sx, sy, sw, sh, dx, dy, dw, dh);
  }
  ctx.restore();
}

function fillGradientBackground(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  effect: 'purple' | 'blue'
) {
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  if (effect === 'purple') {
    gradient.addColorStop(0, '#7c3aed');
    gradient.addColorStop(0.5, '#4c1d95');
    gradient.addColorStop(1, '#0f172a');
  } else {
    gradient.addColorStop(0, '#2563eb');
    gradient.addColorStop(0.5, '#1e3a8a');
    gradient.addColorStop(1, '#0f172a');
  }
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
}

function sampleCenterConfidence(mask: MPMask, invert: boolean): number {
  const mw = mask.width;
  const mh = mask.height;
  if (!mw || !mh) return 0;

  let sum = 0;
  let count = 0;

  if (mask.hasFloat32Array()) {
    const floats = mask.getAsFloat32Array();
    for (let y = Math.floor(mh * 0.25); y < Math.floor(mh * 0.75); y++) {
      for (let x = Math.floor(mw * 0.3); x < Math.floor(mw * 0.7); x++) {
        const value = floats[y * mw + x] ?? 0;
        sum += invert ? 1 - value : value;
        count += 1;
      }
    }
  } else if (mask.hasUint8Array()) {
    const bytes = mask.getAsUint8Array();
    for (let y = Math.floor(mh * 0.25); y < Math.floor(mh * 0.75); y++) {
      for (let x = Math.floor(mw * 0.3); x < Math.floor(mw * 0.7); x++) {
        const value = bytes[y * mw + x] ?? 0;
        sum += value > 0 ? 1 : 0;
        count += 1;
      }
    }
    return count > 0 ? sum / count : 0;
  }

  return count > 0 ? sum / count : 0;
}

function pickPersonMask(result: ImageSegmenterResult): MaskSelection | null {
  if (result.categoryMask) {
    return { mask: result.categoryMask, mode: 'category', invert: false };
  }

  const confidenceMasks = result.confidenceMasks;
  if (!confidenceMasks || confidenceMasks.length === 0) return null;

  let bestMask = confidenceMasks[0];
  let bestScore = -1;
  let bestInvert = false;

  for (const mask of confidenceMasks) {
    for (const invert of [false, true]) {
      const score = sampleCenterConfidence(mask, invert);
      if (score > bestScore) {
        bestScore = score;
        bestMask = mask;
        bestInvert = invert;
      }
    }
  }

  return { mask: bestMask, mode: 'confidence', invert: bestInvert };
}

function createBuffers(width: number, height: number): ProcessorBuffers {
  const bgCanvas = document.createElement('canvas');
  bgCanvas.width = width;
  bgCanvas.height = height;
  const bgCtx = bgCanvas.getContext('2d');
  if (!bgCtx) throw new Error('Background canvas context unavailable');

  const personCanvas = document.createElement('canvas');
  personCanvas.width = width;
  personCanvas.height = height;
  const personCtx = personCanvas.getContext('2d');
  if (!personCtx) throw new Error('Person canvas context unavailable');

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = width;
  maskCanvas.height = height;
  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) throw new Error('Mask canvas context unavailable');

  const softMaskCanvas = document.createElement('canvas');
  softMaskCanvas.width = width;
  softMaskCanvas.height = height;
  const softMaskCtx = softMaskCanvas.getContext('2d');
  if (!softMaskCtx) throw new Error('Soft mask canvas context unavailable');

  return {
    bgCanvas,
    bgCtx,
    personCanvas,
    personCtx,
    maskCanvas,
    maskCtx,
    softMaskCanvas,
    softMaskCtx,
    maskImageData: maskCtx.createImageData(width, height),
  };
}

function updateMaskCanvas(
  maskCtx: CanvasRenderingContext2D,
  softMaskCtx: CanvasRenderingContext2D,
  maskImageData: ImageData,
  selection: MaskSelection,
  width: number,
  height: number,
  mirrored: boolean
) {
  const { mask, mode, invert } = selection;
  const mw = mask.width;
  const mh = mask.height;
  const data = maskImageData.data;

  if (mode === 'category' && mask.hasUint8Array()) {
    const bytes = mask.getAsUint8Array();
    for (let y = 0; y < height; y++) {
      const sy = Math.min(mh - 1, Math.floor((y / height) * mh));
      for (let x = 0; x < width; x++) {
        const normalizedX = mirrored ? 1 - x / width : x / width;
        const sx = Math.min(mw - 1, Math.floor(normalizedX * mw));
        const isPerson = (bytes[sy * mw + sx] ?? 0) > 0;
        const alpha = isPerson ? 255 : 0;
        const idx = (y * width + x) * 4;
        data[idx] = 255;
        data[idx + 1] = 255;
        data[idx + 2] = 255;
        data[idx + 3] = alpha;
      }
    }
  } else if (mask.hasFloat32Array()) {
    const floats = mask.getAsFloat32Array();
    for (let y = 0; y < height; y++) {
      const sy = Math.min(mh - 1, Math.floor((y / height) * mh));
      for (let x = 0; x < width; x++) {
        const normalizedX = mirrored ? 1 - x / width : x / width;
        const sx = Math.min(mw - 1, Math.floor(normalizedX * mw));
        let confidence = floats[sy * mw + sx] ?? 0;
        if (invert) confidence = 1 - confidence;
        const alpha = Math.round(Math.min(1, Math.max(0, confidence)) * 255);
        const idx = (y * width + x) * 4;
        data[idx] = 255;
        data[idx + 1] = 255;
        data[idx + 2] = 255;
        data[idx + 3] = alpha;
      }
    }
  }

  maskCtx.putImageData(maskImageData, 0, 0);
  softMaskCtx.clearRect(0, 0, width, height);
  softMaskCtx.filter = 'blur(5px)';
  softMaskCtx.drawImage(maskCanvas, 0, 0);
  softMaskCtx.filter = 'none';
}

function drawBackgroundLayer(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  width: number,
  height: number,
  effect: VideoBackgroundEffect,
  mirrored: boolean
) {
  ctx.clearRect(0, 0, width, height);

  if (effect === 'transparent') return;

  if (effect === 'purple' || effect === 'blue') {
    fillGradientBackground(ctx, width, height, effect);
    return;
  }

  ctx.filter = 'blur(20px) brightness(0.9)';
  drawCover(ctx, video, 0, 0, width, height, mirrored);
  ctx.filter = 'none';
}

function drawSegmentedFrame(
  outputCtx: CanvasRenderingContext2D,
  buffers: ProcessorBuffers,
  video: HTMLVideoElement,
  selection: MaskSelection,
  width: number,
  height: number,
  effect: VideoBackgroundEffect,
  mirrored: boolean
) {
  const {
    bgCtx,
    personCtx,
    maskCtx,
    softMaskCtx,
    softMaskCanvas,
    maskImageData,
    bgCanvas,
    personCanvas,
  } = buffers;

  drawBackgroundLayer(bgCtx, video, width, height, effect, mirrored);

  personCtx.clearRect(0, 0, width, height);
  drawCover(personCtx, video, 0, 0, width, height, mirrored);

  updateMaskCanvas(maskCtx, softMaskCtx, maskImageData, selection, width, height, mirrored);

  personCtx.globalCompositeOperation = 'destination-in';
  personCtx.drawImage(softMaskCanvas, 0, 0);
  personCtx.globalCompositeOperation = 'source-over';

  outputCtx.clearRect(0, 0, width, height);
  if (effect !== 'transparent') {
    outputCtx.drawImage(bgCanvas, 0, 0);
  }
  outputCtx.drawImage(personCanvas, 0, 0);
}

function drawPlainFrame(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  width: number,
  height: number,
  mirrored: boolean
) {
  ctx.clearRect(0, 0, width, height);
  drawCover(ctx, video, 0, 0, width, height, mirrored);
}

export type ProcessedVideoOutput = {
  stream: MediaStream;
  stop: () => void;
};

export function createProcessedVideoStream(
  sourceVideo: HTMLVideoElement,
  effect: VideoBackgroundEffect,
  segmenter: ImageSegmenter,
  mirrored = true
): ProcessedVideoOutput | null {
  if (sourceVideo.videoWidth === 0 || sourceVideo.videoHeight === 0) return null;

  const width = 1280;
  const height = 720;

  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = width;
  outputCanvas.height = height;
  const outputCtx = outputCanvas.getContext('2d', { alpha: true });
  if (!outputCtx) return null;

  const buffers = createBuffers(width, height);
  let rafId = 0;
  let frameTimestamp = performance.now();
  let hadSuccessfulFrame = false;

  const render = () => {
    if (sourceVideo.readyState >= 2) {
      frameTimestamp += 33;
      try {
        const result = segmentPersonFrame(segmenter, sourceVideo, frameTimestamp);
        const maskSelection = pickPersonMask(result);
        if (maskSelection) {
          drawSegmentedFrame(
            outputCtx,
            buffers,
            sourceVideo,
            maskSelection,
            width,
            height,
            effect,
            mirrored
          );
          hadSuccessfulFrame = true;
        } else if (!hadSuccessfulFrame) {
          drawPlainFrame(outputCtx, sourceVideo, width, height, mirrored);
        }
        result.close();
      } catch (error) {
        console.error('Arka plan işleme hatası:', error);
        if (!hadSuccessfulFrame) {
          drawPlainFrame(outputCtx, sourceVideo, width, height, mirrored);
        }
      }
    }
    rafId = requestAnimationFrame(render);
  };
  render();

  const stream = outputCanvas.captureStream(30);
  return {
    stream,
    stop: () => {
      cancelAnimationFrame(rafId);
      stream.getTracks().forEach((track) => track.stop());
    },
  };
}
